import API_BASE_URL from '../config/api';

console.log('Product API using Base URL:', API_BASE_URL);
const USE_REAL_API = true;

// Helpers to normalize between server and client shapes
const mapStatusToClient = (s) => (s ? String(s).toLowerCase() : 'inactive');
const mapStatusToServer = (s) => (s ? String(s).toUpperCase() : undefined);

const mapToClient = (p) => ({
  id: p._id || p.id,
  name: p.name,
  description: p.description,
  category:
    typeof p.category === 'string'
      ? p.category.toLowerCase() === 'workwear'
        ? 'Workwear'
        : p.category.toLowerCase() === 'paints'
          ? 'Paints'
          : p.category
      : p.category,
  subcategory: p.subcategory,
  price: p?.pricing?.regularPrice ?? p.price ?? 0,
  cost: p?.cost ?? 0,
  sku: p?.sku ?? '',
  stock: p?.stock?.quantity ?? p.stock ?? 0,
  minStock: p?.stock?.lowStockThreshold ?? p.minStock ?? 0,
  status: mapStatusToClient(p?.status),
  images: p?.images || [],
  colors: p?.workwear?.colors || p?.colors || [],
  sizes: p?.workwear?.sizes || p?.sizes || [],
  brand: p?.workwear?.brand || p?.brand || '',
  material: p?.workwear?.material || p?.material || '',
  weight: p?.weight || 0,
  dimensions: p?.dimensions || undefined,
  features: p?.features || [],
  specifications: p?.specifications || {},
  translations: p?.translations || {},
  tags: p?.tags || [],
  createdAt: p?.createdAt,
  updatedAt: p?.updatedAt,
});

const authHeaders = () => {
  const token =
    typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const mapToServer = (d) => {
  const category = d.category ? String(d.category).toLowerCase() : undefined;
  const payload = {
    name: d.name,
    description: d.description,
    category,
    subcategory:
      d.subcategory && String(d.subcategory).trim().length > 0
        ? String(d.subcategory)
        : 'miscellaneous',
    pricing: {
      regularPrice: d.price ?? 0,
      currency: d.currency || undefined,
    },
    cost: d.cost ?? undefined,
    sku:
      d.sku && String(d.sku).trim().length > 0
        ? String(d.sku)
        : `SKU-${Date.now()}`,
    stock: {
      quantity: d.stock ?? 0,
      lowStockThreshold: d.minStock ?? 0,
    },
    status: mapStatusToServer(d.status),
    images: d.images,
    tags: d.tags,
    weight: d.weight,
    dimensions: d.dimensions,
    features: d.features,
    features: d.features,
    specifications: d.specifications,
    translations: d.translations,
  };
  if (category === 'workwear') {
    payload.workwear = {
      colors: d.colors,
      sizes: d.sizes,
      brand: d.brand,
      material: d.material,
    };
  } else {
    // For non-workwear keep legacy fields if provided
    if (d.colors) payload.colors = d.colors;
    if (d.sizes) payload.sizes = d.sizes;
    if (d.brand) payload.brand = d.brand;
    if (d.material) payload.material = d.material;
  }
  console.log('mapToServer payload:', payload);
  return payload;
};

// Mock data for development
const mockProducts = [
  {
    id: 1,
    name: 'Premium Paint Brush Set',
    description:
      'High-quality synthetic bristle brushes for professional painting',
    category: 'Paints',
    subcategory: 'Equipment',
    price: 29.99,
    cost: 15.0,
    sku: 'BRUSH-001',
    stock: 150,
    minStock: 10,
    status: 'active',
    images: ['/assets/product-image.webp'],
    tags: ['professional', 'synthetic', 'brush'],
    createdAt: '2024-01-15T10:30:00Z',
    updatedAt: '2024-01-15T10:30:00Z',
  },
  {
    id: 2,
    name: 'Industrial Primer',
    description: 'Heavy-duty primer for metal surfaces',
    category: 'Paints',
    subcategory: 'Primers',
    price: 45.5,
    cost: 22.75,
    sku: 'PRIMER-002',
    stock: 75,
    minStock: 15,
    status: 'active',
    images: ['/assets/product-image.webp'],
    tags: ['industrial', 'metal', 'primer'],
    createdAt: '2024-01-14T14:20:00Z',
    updatedAt: '2024-01-14T14:20:00Z',
  },
  {
    id: 3,
    name: 'Safety Workwear Jacket',
    description: 'High-visibility safety jacket for construction workers',
    category: 'Workwear',
    subcategory: 'Jackets',
    price: 89.99,
    cost: 45.0,
    sku: 'JACKET-003',
    stock: 25,
    minStock: 5,
    status: 'active',
    images: ['/assets/product-image.webp'],
    tags: ['safety', 'high-vis', 'workwear'],
    colors: ['Black', 'Navy Blue', 'Orange'],
    sizes: ['S', 'M', 'L', 'XL', 'XXL'],
    createdAt: '2024-01-13T09:15:00Z',
    updatedAt: '2024-01-13T09:15:00Z',
  },
  {
    id: 4,
    name: 'Professional Polo Shirt',
    description: 'Comfortable cotton polo shirt for work',
    category: 'Workwear',
    subcategory: 'Polo Shirts',
    price: 24.99,
    cost: 12.5,
    sku: 'POLO-004',
    stock: 100,
    minStock: 10,
    status: 'active',
    images: ['/assets/product-image.webp'],
    tags: ['cotton', 'comfortable', 'professional'],
    colors: ['White', 'Navy Blue', 'Black', 'Gray'],
    sizes: ['S', 'M', 'L', 'XL', 'XXL'],
    createdAt: '2024-01-12T16:45:00Z',
    updatedAt: '2024-01-12T16:45:00Z',
  },
  {
    id: 5,
    name: 'Safety Work Boots',
    description: 'Steel-toed safety boots for construction work',
    category: 'Workwear',
    subcategory: 'Shoes',
    price: 79.99,
    cost: 40.0,
    sku: 'BOOTS-005',
    stock: 50,
    minStock: 5,
    status: 'active',
    images: ['/assets/product-image.webp'],
    tags: ['steel-toed', 'safety', 'construction'],
    colors: ['Black', 'Brown'],
    sizes: ['8', '9', '10', '11', '12', '13'],
    createdAt: '2024-01-11T11:30:00Z',
    updatedAt: '2024-01-11T11:30:00Z',
  },
];

// Generate additional products to reach 100+
const generateAdditionalProducts = () => {
  const additionalProducts = [];
  let id = 6;

  // Generate more paint products
  const paintSubcategories = [
    'Primers',
    'Varnishes',
    'Thinners',
    'Degreasers',
    'Stone Chip Protectors',
    'Ready Mix',
    'Sealants',
    'Spray Products',
    'Abrasives',
    'Masking',
    'Equipment',
    'Miscellaneous',
  ];
  const paintNames = [
    'Professional Paint Brush',
    'Industrial Primer',
    'Acrylic Varnish',
    'Paint Thinner',
    'Metal Degreaser',
    'Stone Chip Coating',
    'Ready Mix Paint',
    'Silicone Sealant',
    'Spray Paint',
    'Sandpaper',
    'Masking Tape',
    'Paint Roller',
    'Drop Cloth',
    'Paint Tray',
    'Paint Stirrer',
  ];

  for (let i = 0; i < 30; i++) {
    const subcategory = paintSubcategories[i % paintSubcategories.length];
    const name = paintNames[i % paintNames.length] + ` ${i + 1}`;
    additionalProducts.push({
      id: id++,
      name,
      description: `High-quality ${subcategory.toLowerCase()} for professional use`,
      category: 'Paints',
      subcategory,
      price: Math.round((Math.random() * 50 + 10) * 100) / 100,
      cost: Math.round((Math.random() * 25 + 5) * 100) / 100,
      sku: `PAINT-${id.toString().padStart(3, '0')}`,
      stock: Math.floor(Math.random() * 100 + 10),
      minStock: Math.floor(Math.random() * 10 + 5),
      status: Math.random() > 0.1 ? 'active' : 'inactive',
      images: ['/assets/product-image.webp'],
      tags: [subcategory.toLowerCase(), 'professional', 'quality'],
      createdAt: new Date(
        Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000
      ).toISOString(),
      updatedAt: new Date(
        Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000
      ).toISOString(),
    });
  }

  // Generate more workwear products
  const workwearSubcategories = [
    'Polo Shirts',
    'Jackets',
    'Shoes',
    'Pants',
    'T-shirts',
    'Shorts',
  ];
  const workwearNames = [
    'Safety Jacket',
    'Polo Shirt',
    'Work Boots',
    'Cargo Pants',
    'Cotton T-Shirt',
    'Work Shorts',
    'High-Vis Vest',
    'Safety Helmet',
    'Work Gloves',
    'Coveralls',
    'Apron',
    'Belt',
  ];
  const workwearColors = [
    'Black',
    'White',
    'Navy Blue',
    'Gray',
    'Orange',
    'Red',
  ];
  const workwearSizes = ['S', 'M', 'L', 'XL', 'XXL'];
  const shoeSizes = ['8', '9', '10', '11', '12', '13'];

  for (let i = 0; i < 30; i++) {
    const subcategory = workwearSubcategories[i % workwearSubcategories.length];
    const name = workwearNames[i % workwearNames.length] + ` ${i + 1}`;
    const isShoes = subcategory === 'Shoes';
    const sizes = isShoes ? shoeSizes : workwearSizes;
    const selectedColors = workwearColors.slice(
      0,
      Math.floor(Math.random() * 4) + 2
    );
    const selectedSizes = sizes.slice(0, Math.floor(Math.random() * 4) + 3);

    additionalProducts.push({
      id: id++,
      name,
      description: `Professional ${subcategory.toLowerCase()} for work environment`,
      category: 'Workwear',
      subcategory,
      price: Math.round((Math.random() * 80 + 15) * 100) / 100,
      cost: Math.round((Math.random() * 40 + 8) * 100) / 100,
      sku: `WEAR-${id.toString().padStart(3, '0')}`,
      stock: Math.floor(Math.random() * 80 + 10),
      minStock: Math.floor(Math.random() * 8 + 3),
      status: Math.random() > 0.1 ? 'active' : 'inactive',
      images: ['/assets/product-image.webp'],
      tags: [subcategory.toLowerCase(), 'professional', 'work'],
      colors: selectedColors,
      sizes: selectedSizes,
      brand: ['Nike', 'Adidas', 'Carhartt', 'Dickies', 'Caterpillar'][
        Math.floor(Math.random() * 5)
      ],
      material: ['Cotton', 'Polyester', 'Leather', 'Canvas', 'Denim'][
        Math.floor(Math.random() * 5)
      ],
      weight: Math.round((Math.random() * 2 + 0.5) * 100) / 100,
      dimensions: {
        length: Math.floor(Math.random() * 50 + 20),
        width: Math.floor(Math.random() * 30 + 15),
        height: Math.floor(Math.random() * 10 + 5),
      },
      warranty: ['6 months', '1 year', '2 years', 'No warranty'][
        Math.floor(Math.random() * 4)
      ],
      features: ['Durable', 'Comfortable', 'Professional', 'Safety'],
      specifications: {
        Material: ['Cotton', 'Polyester', 'Leather'][
          Math.floor(Math.random() * 3)
        ],
        Care: 'Machine washable',
        Origin: 'Made in USA',
      },
      createdAt: new Date(
        Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000
      ).toISOString(),
      updatedAt: new Date(
        Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000
      ).toISOString(),
    });
  }

  return additionalProducts;
};

// Combine original products with generated ones
const allProducts = [...mockProducts, ...generateAdditionalProducts()];

// Simulate API delay
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Category and attribute constants
const categories = ['Paints', 'Workwear'];

const workwearSubcategories = [
  'Polo Shirts',
  'Jackets',
  'Shoes',
  'Pants',
  'T-shirts',
  'Shorts',
];

const paintSubcategories = [
  'Primers',
  'Varnishes',
  'Thinners',
  'Degreasers',
  'Stone Chip Protectors',
  'Ready Mix',
  'Sealants',
  'Spray Products',
  'Abrasives',
  'Masking',
  'Equipment',
  'Miscellaneous',
];

const colors = [
  'Black',
  'White',
  'Navy Blue',
  'Royal Blue',
  'Red',
  'Orange',
  'Yellow',
  'Green',
  'Gray',
  'Brown',
  'Purple',
  'Pink',
];

const sizes = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'];

const shoeSizes = [
  '6',
  '6.5',
  '7',
  '7.5',
  '8',
  '8.5',
  '9',
  '9.5',
  '10',
  '10.5',
  '11',
  '11.5',
  '12',
  '12.5',
  '13',
  '13.5',
  '14',
];

// GET /api/products - List products with pagination, search, filters
export const fetchProducts = async (params = new URLSearchParams()) => {
  if (USE_REAL_API) {
    const url = new URL(`${API_BASE_URL}/products`);
    // copy provided params to the URL for server-side filtering when available
    if (params && typeof params.forEach === 'function') {
      params.forEach((v, k) => url.searchParams.set(k, v));
    }
    const res = await fetch(url.toString(), { method: 'GET' });
    const json = await res.json();
    if (!res.ok) throw new Error(json?.message || 'Failed to fetch products');
    let serverProducts = (json?.data || []).map(mapToClient);

    // Apply filters client-side to preserve Admin UI behavior
    const search = params.get('search');
    if (search) {
      const s = search.toLowerCase();
      serverProducts = serverProducts.filter(
        (product) =>
          (product.name || '').toLowerCase().includes(s) ||
          (product.description || '').toLowerCase().includes(s) ||
          (product.sku || '').toLowerCase().includes(s)
      );
    }
    const category = params.get('category');
    if (category) {
      serverProducts = serverProducts.filter(
        (p) => (p.category || '').toLowerCase() === category.toLowerCase()
      );
    }
    const subcategory = params.get('subcategory');
    if (subcategory) {
      serverProducts = serverProducts.filter(
        (p) => (p.subcategory || '').toLowerCase() === subcategory.toLowerCase()
      );
    }
    const status = params.get('status');
    if (status) {
      serverProducts = serverProducts.filter(
        (p) => (p.status || '').toLowerCase() === status.toLowerCase()
      );
    }
    // Tag filters: tag=one&tag=two or tags=one,two
    const tagParams = params.getAll('tag');
    const tagsParam = params.get('tags');
    const allTags = [
      ...tagParams,
      ...(tagsParam
        ? tagsParam
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean)
        : []),
    ].map((t) => t.toLowerCase());
    if (allTags.length > 0) {
      serverProducts = serverProducts.filter((p) => {
        const ptags = (p.tags || []).map((x) => String(x).toLowerCase());
        return allTags.every((t) => ptags.includes(t));
      });
    }
    const sortBy = params.get('sortBy') || 'name';
    const sortOrder = params.get('sortOrder') || 'asc';
    serverProducts.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }
      if (sortOrder === 'asc') return aVal > bVal ? 1 : -1;
      return aVal < bVal ? 1 : -1;
    });
    const page = parseInt(params.get('page')) || 1;
    const limit = parseInt(params.get('limit')) || 10;
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginated = serverProducts.slice(startIndex, endIndex);
    return {
      data: paginated,
      total: serverProducts.length,
      totalPages: Math.ceil(serverProducts.length / limit),
      page,
      limit,
    };
  }

  await delay(500); // mock fallback
  let filteredProducts = [...allProducts];
  const search = params.get('search');
  if (search) {
    filteredProducts = filteredProducts.filter(
      (product) =>
        product.name.toLowerCase().includes(search.toLowerCase()) ||
        product.description.toLowerCase().includes(search.toLowerCase()) ||
        product.sku.toLowerCase().includes(search.toLowerCase())
    );
  }
  const category = params.get('category');
  if (category) {
    filteredProducts = filteredProducts.filter(
      (product) => product.category.toLowerCase() === category.toLowerCase()
    );
  }
  const subcategory = params.get('subcategory');
  if (subcategory) {
    filteredProducts = filteredProducts.filter(
      (product) =>
        product.subcategory &&
        product.subcategory.toLowerCase() === subcategory.toLowerCase()
    );
  }
  const status = params.get('status');
  if (status) {
    filteredProducts = filteredProducts.filter(
      (product) => product.status === status
    );
  }
  // Tag filters: tag=one&tag=two or tags=one,two
  const tagParams = params.getAll('tag');
  const tagsParam = params.get('tags');
  const allTags = [
    ...tagParams,
    ...(tagsParam
      ? tagsParam
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean)
      : []),
  ].map((t) => t.toLowerCase());
  if (allTags.length > 0) {
    filteredProducts = filteredProducts.filter((p) => {
      const ptags = (p.tags || []).map((x) => String(x).toLowerCase());
      return allTags.every((t) => ptags.includes(t));
    });
  }
  const sortBy = params.get('sortBy') || 'name';
  const sortOrder = params.get('sortOrder') || 'asc';
  filteredProducts.sort((a, b) => {
    let aVal = a[sortBy];
    let bVal = b[sortBy];
    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }
    if (sortOrder === 'asc') return aVal > bVal ? 1 : -1;
    return aVal < bVal ? 1 : -1;
  });
  const page = parseInt(params.get('page')) || 1;
  const limit = parseInt(params.get('limit')) || 10;
  const startIndex = (page - 1) * limit;
  const endIndex = startIndex + limit;
  const paginatedProducts = filteredProducts.slice(startIndex, endIndex);
  return {
    data: paginatedProducts,
    total: filteredProducts.length,
    totalPages: Math.ceil(filteredProducts.length / limit),
    page,
    limit,
  };
};

// GET /api/products/:id - Get single product
export const fetchProduct = async (id) => {
  if (USE_REAL_API) {
    const res = await fetch(`${API_BASE_URL}/products/${id}`);
    const json = await res.json();
    if (!res.ok) throw new Error(json?.message || 'Failed to fetch product');
    const prod = json?.data?.product || json?.data || json;
    if (!prod) throw new Error('Product not found');
    return mapToClient(prod);
  }
  await delay(300);
  const product = allProducts.find((p) => p.id === parseInt(id));
  if (!product) throw new Error('Product not found');
  return product;
};

// POST /api/products - Create product
export const createProduct = async (productData) => {
  if (USE_REAL_API) {
    const res = await fetch(`${API_BASE_URL}/products`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(mapToServer(productData)),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json?.message || 'Failed to create product');
    const p = json?.data;
    return mapToClient(p);
  }
  await delay(800);
  const newProduct = {
    id: allProducts.length + 1,
    ...productData,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  allProducts.push(newProduct);
  return newProduct;
};

// PUT /api/products/:id - Update product
export const updateProduct = async (id, productData) => {
  if (USE_REAL_API) {
    const res = await fetch(`${API_BASE_URL}/products/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(mapToServer(productData)),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json?.message || 'Failed to update product');
    const p = json?.data;
    return mapToClient(p);
  }
  await delay(800);
  const index = allProducts.findIndex((p) => p.id === parseInt(id));
  if (index === -1) throw new Error('Product not found');
  allProducts[index] = {
    ...allProducts[index],
    ...productData,
    updatedAt: new Date().toISOString(),
  };
  return allProducts[index];
};

// DELETE /api/products/:id - Delete product
export const deleteProduct = async (id) => {
  if (USE_REAL_API) {
    const res = await fetch(`${API_BASE_URL}/products/${id}`, {
      method: 'DELETE',
      headers: { ...authHeaders() },
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json?.message || 'Failed to delete product');
    return { success: true };
  }
  await delay(500);
  const index = allProducts.findIndex((p) => p.id === parseInt(id));
  if (index === -1) throw new Error('Product not found');
  allProducts.splice(index, 1);
  return { success: true };
};

// POST /api/products/import - Batch import via CSV
export const importProducts = async (file) => {
  if (USE_REAL_API) {
    const formData = new FormData();
    formData.append('csvFile', file);
    const res = await fetch(`${API_BASE_URL}/products/import`, {
      method: 'POST',
      headers: { ...authHeaders() },
      body: formData,
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json?.message || 'Failed to import products');
    return { success: true, imported: json?.data?.imported || 0 };
  }
  await delay(2000);
  return { success: true, imported: 2 };
};

// GET /api/inventory/:id - Check stock of product
export const fetchInventory = async (id) => {
  await delay(300);
  const product = allProducts.find((p) => p.id === parseInt(id));
  if (!product) {
    throw new Error('Product not found');
  }

  return {
    productId: product.id,
    sku: product.sku,
    currentStock: product.stock,
    minStock: product.minStock,
    status: product.stock > product.minStock ? 'in_stock' : 'low_stock',
    lastUpdated: product.updatedAt,
  };
};

// PUT /api/inventory/:id - Adjust stock manually
export const updateInventory = async (id, stockData) => {
  if (USE_REAL_API) {
    // Reuse updateProduct route
    return updateProduct(id, {
      stock: stockData.stock,
      minStock: stockData.minStock,
    });
  }
  await delay(500);
  const index = allProducts.findIndex((p) => p.id === parseInt(id));
  if (index === -1) throw new Error('Product not found');
  allProducts[index] = {
    ...allProducts[index],
    stock: stockData.stock,
    minStock: stockData.minStock || allProducts[index].minStock,
    updatedAt: new Date().toISOString(),
  };
  return allProducts[index];
};

// Get categories for filters
export const fetchCategories = async () => {
  if (USE_REAL_API) {
    const res = await fetch(`${API_BASE_URL}/products/categories`);
    const json = await res.json();
    if (!res.ok) throw new Error(json?.message || 'Failed to fetch categories');
    return json?.data || [];
  }
  await delay(200);
  return categories;
};

// Get subcategories based on category
export const fetchSubcategories = async (category) => {
  if (USE_REAL_API) {
    const url = new URL(`${API_BASE_URL}/products/subcategories`);
    if (category) url.searchParams.set('category', category);
    const res = await fetch(url.toString());
    const json = await res.json();
    if (!res.ok)
      throw new Error(json?.message || 'Failed to fetch subcategories');
    const apiList = (json?.data || []).map((s) => String(s));
    // Curate known lists for Paints/Workwear, merge API extras (excluding 'miscellaneous')
    if (category === 'Paints') {
      const curated = [
        'Mastics / Putties',
        'Degreasers',
        'Abrasives',
        'Fillers / Apprêts',
        'Ready Mix / Finished Paint',
        'Masking Products',
        'Primers',
        'Stone Chip Protectors',
        'Miscellaneous / Accessories',
        'Varnishes / Clearcoats',
        'Sealants',
        'Paint Equipment / Tools',
        'Thinners / Reducers',
        'Spray Products / Aerosols',
        'Base Coats (Color Mixing System)',
      ];
      const extras = apiList.filter(
        (s) =>
          s.trim().toLowerCase() !== 'miscellaneous' && !curated.includes(s)
      );
      return [...curated, ...extras];
    }
    if (category === 'Workwear') {
      const curated = [
        'Polo Shirts',
        'Jackets',
        'Shoes',
        'Pants',
        'T-shirts',
        'Shorts',
      ];
      const extras = apiList.filter(
        (s) =>
          s.trim().toLowerCase() !== 'miscellaneous' && !curated.includes(s)
      );
      return [...curated, ...extras];
    }
    return apiList;
  }
  await delay(200);
  if (category === 'Workwear') return workwearSubcategories;
  if (category === 'Paints') return paintSubcategories;
  return [];
};

// Get colors for workwear
export const fetchColors = async () => {
  await delay(200);
  return colors;
};

// Get sizes based on subcategory
export const fetchSizes = async (subcategory) => {
  await delay(200);
  if (subcategory === 'Shoes') {
    return shoeSizes;
  }
  return sizes;
};

// Get product statuses for filters
export const fetchProductStatuses = async () => {
  if (USE_REAL_API) {
    const res = await fetch(`${API_BASE_URL}/products/statuses`);
    const json = await res.json();
    if (!res.ok) throw new Error(json?.message || 'Failed to fetch statuses');
    return json?.data || [];
  }
  await delay(200);
  return ['active', 'inactive', 'out_of_stock', 'discontinued'];
};

// Upload multiple images via API and return an array of URLs
export const uploadImages = async (files) => {
  const formData = new FormData();
  for (const file of files) formData.append('images', file);
  const res = await fetch(`${API_BASE_URL}/upload/images`, {
    method: 'POST',
    headers: { ...authHeaders() },
    body: formData,
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.message || 'Failed to upload images');
  const list = json?.data || [];
  return list.map((x) => x.url).filter(Boolean);
};
